import { getCurrentIdToken, triggerSignIn } from "./auth";
import type { Artifact } from "./components/artifacts/types";
import type { ThinkingEffort } from "./components/chat/ThinkingEffortPicker";
import { config } from "./config";

export function authHeaders(): HeadersInit {
  const headers: Record<string, string> = {};
  const token = getCurrentIdToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else if (config.devUser) {
    // Local-dev fallback: backend AUTH_MODE=dev consumes X-Dev-User.
    headers["X-Dev-User"] = config.devUser;
  }
  return headers;
}

function handleUnauthorized(status: number): void {
  if (status !== 401) return;
  // Token expired or missing in a deployed env — kick back to the IdP.
  // No-op in local dev (no sign-in trigger registered).
  triggerSignIn();
}

async function jsonGet<T>(path: string): Promise<T> {
  const r = await fetch(`${config.backendUrl}${path}`, { headers: authHeaders() });
  if (!r.ok) {
    handleUnauthorized(r.status);
    throw new Error(`GET ${path} failed: ${r.status} ${await r.text()}`);
  }
  return (await r.json()) as T;
}

async function jsonSend<T>(method: "POST" | "PUT", path: string, body: unknown): Promise<T> {
  const r = await fetch(`${config.backendUrl}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    handleUnauthorized(r.status);
    throw new Error(`${method} ${path} failed: ${r.status} ${await r.text()}`);
  }
  return (await r.json()) as T;
}

export async function fetchResult(resultRef: string): Promise<unknown> {
  return jsonGet(`/api/results/${encodeURI(resultRef)}`);
}

export async function fetchArtifact(conversationId: string, artifactId: string): Promise<unknown> {
  return jsonGet(`/api/conversations/${conversationId}/artifacts/${artifactId}`);
}

export type ModelEntry = {
  label: string;
  model_id: string;
  supported_efforts: ThinkingEffort[];
};

export type ModelsResponse = {
  effort_levels: ThinkingEffort[];
  models: ModelEntry[];
};

export async function fetchModels(): Promise<ModelsResponse> {
  return jsonGet<ModelsResponse>("/api/models");
}

export type ConversationSummary = {
  conversation_id: string;
  title: string;
  last_message_at: string;
  model_id: string;
};

export async function fetchConversations(): Promise<ConversationSummary[]> {
  return jsonGet<ConversationSummary[]>("/api/conversations");
}

export async function deleteConversation(conversationId: string): Promise<void> {
  const r = await fetch(`${config.backendUrl}/api/conversations/${conversationId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!r.ok && r.status !== 204) {
    handleUnauthorized(r.status);
    throw new Error(`DELETE /api/conversations/${conversationId} failed: ${r.status}`);
  }
}

export type UploadTicket = {
  attachment_id: string;
  filename: string;
  content_type: string;
  format: string;
  byte_size: number;
  upload_url: string;
  expires_in_seconds: number;
};

export type UploadLimits = {
  max_bytes: number;
  /**
   * Per-extension caps where they differ from `max_bytes`: PowerPoint and Excel
   * files are parsed by the backend's file tools rather than sent to Bedrock.
   */
  max_bytes_by_extension?: Record<string, number>;
  max_documents_per_request: number;
  extensions: string[];
};

export async function fetchUploadLimits(): Promise<UploadLimits> {
  return jsonGet<UploadLimits>("/api/uploads/limits");
}

/**
 * Stage a file for the next turn: reserve an id, then PUT the bytes straight
 * to S3 on the presigned URL so they never pass through the backend.
 */
export async function uploadAttachment(file: File): Promise<UploadTicket> {
  const ticket = await jsonSend<UploadTicket>("POST", "/api/uploads", {
    filename: file.name,
    byte_size: file.size,
  });
  const put = await fetch(ticket.upload_url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": ticket.content_type },
  });
  if (!put.ok) {
    throw new Error(`upload of ${file.name} failed: ${put.status}`);
  }
  return ticket;
}

export type PersistedAttachment = {
  attachment_id: string;
  filename: string;
  content_type: string;
  format: string;
  byte_size: number;
};

export type PersistedToolEvent = {
  tool_call_id: string;
  tool: string;
  status: "ok" | "error" | "pending";
  input_summary: string;
  summary?: string | null;
  error_message?: string | null;
  duration_ms?: number;
  surface?: "artifact" | "tool_only";
  text_offset: number;
  result_payload?: Record<string, unknown> | null;
};

export type PersistedMessage = {
  conversation_id: string;
  seq: number;
  role: "user" | "assistant" | "tool";
  content: {
    text?: string;
    attachment?: PersistedAttachment;
    toolUse?: unknown;
    toolResult?: unknown;
  }[];
  tokens_in: number;
  tokens_out: number;
  model_id?: string | null;
  effort?: string | null;
  created_at: string;
  artifacts?: { spec: Artifact; text_offset: number }[];
  tool_events?: PersistedToolEvent[];
};

export async function fetchMessages(conversationId: string): Promise<PersistedMessage[]> {
  return jsonGet<PersistedMessage[]>(`/api/conversations/${conversationId}/messages`);
}

export type Memory = {
  user_sub: string;
  content: string;
  updated_at: string;
};

export async function fetchMemory(): Promise<Memory> {
  return jsonGet<Memory>("/api/memory");
}

export async function saveMemory(content: string): Promise<Memory> {
  return jsonSend<Memory>("PUT", "/api/memory", { content });
}

export type ConversationUsage = {
  conversation_id: string;
  message_count: number;
  total_input_tokens: number;
  total_output_tokens: number;
  last_input_tokens: number;
};

export async function fetchUsage(conversationId: string): Promise<ConversationUsage> {
  return jsonGet<ConversationUsage>(`/api/conversations/${conversationId}/usage`);
}

export type CompactionResult = {
  conversation_id: string;
  summary: string;
  messages_replaced: number;
};

export async function compactConversation(conversationId: string): Promise<CompactionResult> {
  return jsonSend<CompactionResult>("POST", `/api/conversations/${conversationId}/compact`, {});
}
